// WebAuthn / Passkey browser helpers (Face ID, Touch ID, fingerprint).
import { getToken } from './authStorage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const isWebAuthnSupported = () =>
  typeof window !== 'undefined' &&
  !!(window.PublicKeyCredential && navigator.credentials && navigator.credentials.create);

export const isPlatformAuthenticatorAvailable = async () => {
  try {
    if (!isWebAuthnSupported()) return false;
    if (!window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable) return false;
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch { return false; }
};

const b64uToBuf = (s) => {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((s.length + 3) % 4);
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
};

const bufToB64u = (buf) => {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

// Convert base64url strings in the options coming from the server into ArrayBuffers.
const prepareCreationOptions = (opts) => {
  const o = { ...opts };
  o.challenge = b64uToBuf(opts.challenge);
  o.user = { ...opts.user, id: b64uToBuf(opts.user.id) };
  if (opts.excludeCredentials) {
    o.excludeCredentials = opts.excludeCredentials.map((c) => ({ ...c, id: b64uToBuf(c.id) }));
  }
  return o;
};

const prepareRequestOptions = (opts) => {
  const o = { ...opts };
  o.challenge = b64uToBuf(opts.challenge);
  if (opts.allowCredentials) {
    o.allowCredentials = opts.allowCredentials.map((c) => ({ ...c, id: b64uToBuf(c.id) }));
  }
  return o;
};

const credentialToJSON = (cred) => {
  const r = cred.response;
  const out = {
    id: cred.id,
    rawId: bufToB64u(cred.rawId),
    type: cred.type,
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
    authenticatorAttachment: cred.authenticatorAttachment || undefined,
    response: {},
  };
  if (r.clientDataJSON) out.response.clientDataJSON = bufToB64u(r.clientDataJSON);
  if (r.attestationObject) out.response.attestationObject = bufToB64u(r.attestationObject);
  if (r.authenticatorData) out.response.authenticatorData = bufToB64u(r.authenticatorData);
  if (r.signature) out.response.signature = bufToB64u(r.signature);
  if (r.userHandle) out.response.userHandle = bufToB64u(r.userHandle);
  if (typeof r.getTransports === 'function') {
    try { out.response.transports = r.getTransports(); } catch { /* ignore */ }
  }
  return out;
};

// Register a passkey for the currently logged-in user.
export const registerPasskey = async (deviceLabel = '') => {
  const token = getToken();
  const beginRes = await fetch(`${BACKEND_URL}/api/webauthn/registration/begin`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: '{}',
  });
  if (!beginRes.ok) throw new Error(await beginRes.text().catch(() => 'Errore avvio registrazione'));
  const { publicKey, ceremony_id } = await beginRes.json();
  const cred = await navigator.credentials.create({ publicKey: prepareCreationOptions(publicKey) });
  const completeRes = await fetch(`${BACKEND_URL}/api/webauthn/registration/complete`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ ceremony_id, credential: credentialToJSON(cred), device_label: deviceLabel }),
  });
  if (!completeRes.ok) throw new Error(await completeRes.text().catch(() => 'Errore registrazione passkey'));
  return completeRes.json();
};

// Log in using an existing passkey. email optional (empty => discoverable).
export const loginWithPasskey = async (email = '', remember = true) => {
  const beginRes = await fetch(`${BACKEND_URL}/api/webauthn/authentication/begin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!beginRes.ok) throw new Error('Nessuna passkey disponibile');
  const { publicKey, ceremony_id } = await beginRes.json();
  const cred = await navigator.credentials.get({ publicKey: prepareRequestOptions(publicKey) });
  const completeRes = await fetch(`${BACKEND_URL}/api/webauthn/authentication/complete`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ceremony_id, credential: credentialToJSON(cred), remember }),
  });
  if (!completeRes.ok) throw new Error('Autenticazione biometrica fallita');
  return completeRes.json();
};

export const listPasskeys = async () => {
  const token = getToken();
  const res = await fetch(`${BACKEND_URL}/api/webauthn/credentials`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Errore nel caricamento delle passkey');
  return res.json();
};

export const deletePasskey = async (id) => {
  const token = getToken();
  const res = await fetch(`${BACKEND_URL}/api/webauthn/credentials/${id}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Errore nella rimozione della passkey');
  return res.json();
};
